
import { supabase } from '@/integrations/supabase/client';

/**
 * Save an email to the waitlist database
 * @param email Email address to save
 * @param metadata Optional metadata about the signup
 * @returns Success status of the operation
 */
export const saveWaitlistEmail = async (
  email: string, 
  metadata?: { 
    name?: string, 
    country_code?: string,
    source?: string,
    [key: string]: any 
  }
) => {
  try {
    // Clean up and normalize email
    const cleanEmail = email.toLowerCase().trim();

    // First, check if the email already exists to provide a better user experience
    const { data: existingEmails, error: checkError } = await supabase
      .from('waitlist_emails')
      .select('email')
      .eq('email', cleanEmail)
      .limit(1);
    
    if (checkError) {
      console.error('Error checking existing waitlist email:', checkError);
      return { success: false, error: checkError };
    }
    
    // If email already exists, return success with alreadyExists flag
    if (existingEmails && existingEmails.length > 0) {
      console.log('Email already on waitlist:', cleanEmail);
      return { success: true, alreadyExists: true };
    }

    // Get IP address from client if available (optional)
    let ipAddress;
    try {
      const response = await fetch('https://api.ipify.org?format=json');
      const data = await response.json();
      ipAddress = data.ip;
    } catch (e) {
      console.log('Could not fetch IP address:', e);
    }

    // Save the email to the waitlist
    const { data, error } = await supabase
      .from('waitlist_emails')
      .insert({
        email: cleanEmail,
        name: metadata?.name,
        country_code: metadata?.country_code || 'KY', // Default to Cayman Islands
        source: metadata?.source || 'cayman_landing',
        ip_address: ipAddress,
        metadata: metadata ? JSON.stringify(metadata) : '{}'
      })
      .select();

    if (error) {
      // Handle permission errors more gracefully
      if (error.code === '42501' || error.code === '401') { // PostgreSQL permission error or Supabase 401
        console.error('Permission denied when saving waitlist email. Check RLS policies.');
        return { success: false, error: 'Permission error', details: error };
      }
      
      // Handle unique constraint violations gracefully
      if (error.code === '23505') { // PostgreSQL unique violation code
        console.log('Email already on waitlist:', cleanEmail);
        return { success: true, alreadyExists: true };
      }
      
      console.error('Error saving waitlist email:', error);
      return { success: false, error };
    }

    console.log('Email added to waitlist successfully:', cleanEmail);
    return { success: true, data };
  } catch (error) {
    console.error('Exception in saveWaitlistEmail:', error);
    return { success: false, error };
  }
};

/**
 * Get count of people on waitlist
 * @returns Number of people on waitlist or null if error
 */
export const getWaitlistCount = async () => {
  try {
    const { count, error } = await supabase
      .from('waitlist_emails')
      .select('*', { count: 'exact', head: true });
    
    if (error) {
      console.error('Error getting waitlist count:', error);
      return null;
    }
    
    return count;
  } catch (error) {
    console.error('Exception in getWaitlistCount:', error);
    return null;
  }
};

/**
 * Get stats about waitlist notifications
 * @returns Statistics about notification status
 */
export const getWaitlistNotificationStats = async () => {
  try {
    // Get total number of emails
    const { count: totalCount, error: totalError } = await supabase
      .from('waitlist_emails')
      .select('*', { count: 'exact', head: true });
    
    if (totalError) {
      console.error('Error getting total waitlist count:', totalError);
      return null;
    }
    
    // Get number of notified emails
    const { count: notifiedCount, error: notifiedError } = await supabase
      .from('waitlist_emails')
      .select('*', { count: 'exact', head: true })
      .not('notified_at', 'is', null);
    
    if (notifiedError) {
      console.error('Error getting notified waitlist count:', notifiedError);
      return null;
    }
    
    return {
      total: totalCount,
      notified: notifiedCount,
      pending: totalCount! - notifiedCount!
    };
  } catch (error) {
    console.error('Exception in getWaitlistNotificationStats:', error);
    return null;
  }
};
